import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useNotifications } from '@/data/Content/Notifications';
import { useDpCatalogoContext } from '@/data/context/dpcatalogo';
import { useSettings } from '@/data/Settings';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { processError } from '@/data/utils/processError';
import { transactionsDpCatalogo } from 'integration/generated/transactions';
import { RequestParams } from 'integration/generated/transactions/http-client';
import { useCallback, useEffect, useMemo } from 'react';
import useSWR from 'swr';

export const dpCatalogoGetDistributorByTokenFetcher =
	(pub: boolean) => async (storeId: string, token: string, params: RequestParams) => {
		const response = await transactionsDpCatalogo(pub).dpCatalogoGetDistributorByToken(
			storeId,
			token,
			params
		);
		return response;
	};

const DATA_KEY_DP_CATALOGO_GET_DISTRIBUTOR_BY_TOKEN = 'dpCatalogoGetDistributorByToken';

export const useDpCatalogoGetDistributorByToken = (token?: string) => {
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();

	const { data, ...rest } = useSWR(
		storeId && token ? [{ storeId, token }, DATA_KEY_DP_CATALOGO_GET_DISTRIBUTOR_BY_TOKEN] : null,
		async ([{ storeId, token }]) =>
			dpCatalogoGetDistributorByTokenFetcher(true)(storeId, token, params)
	);

	return {
		data,
		...rest,
	};
};

export const useDpCatalogoDistributor = () => {
	const router = useNextRouter();
	const { distributor, onSetDistributor } = useDpCatalogoContext();

	const token = useMemo(
		() => router.query?.token?.toString() || distributor?.token || undefined,
		[router.query.token, distributor?.token]
	);

	const response = useDpCatalogoGetDistributorByToken(token);

	useEffect(() => {
		if (response.data) {
			onSetDistributor({ ...response.data, token });
		}
	}, [response.data, onSetDistributor, token]);

	return { ...response, data: { ...response.data, token } };
};

export const dpCatalogoSendOrderFetcher =
	(pub: boolean) =>
	async (
		storeId: string,
		orderId: string,
		data: {
			token: string;
			clientFullName: string;
			clientEmail: string;
			clientPhone: string;
		},
		params: RequestParams
	) => {
		const response = await transactionsDpCatalogo(pub).dpCatalogoSendOrder(
			storeId,
			orderId,
			data,
			params
		);
		return response;
	};

export const useDpCatalogoSendOrder = () => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { storeId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { notifyError } = useNotifications();

	const sendOrder = useCallback(
		async (data: {
			orderId: string;
			token: string;
			clientFullName: string;
			clientEmail: string;
			clientPhone: string;
		}) => {
			try {
				await dpCatalogoSendOrderFetcher(true)(
					storeId,
					data.orderId,
					{
						token: data.token,
						clientFullName: data.clientFullName,
						clientEmail: data.clientEmail,
						clientPhone: data.clientPhone,
					},
					params
				);
				return { success: true };
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
				return { success: false };
			}
		},
		[storeId, params, notifyError]
	);

	return { sendOrder };
};
